import { ColumnDef } from "@tanstack/react-table";
import AdvanceTable from "components/base/AdvanceTable";
import AdvanceTableFooter from "components/base/AdvanceTableFooter";
import Badge from "components/base/Badge";
import Button from "components/base/Button";
import {
  VendaType,
  formatarDataVenda,
  formatarValor,
  formatarStatusVenda,
  getStatusVendaClass,
} from "data/venda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import VendaExpandedRow from "./VendaExpandedRow";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAdvanceTableContext } from "providers/AdvanceTableProvider";

export const vendaTableColumns: ColumnDef<VendaType>[] = [
  {
    id: "expander",
    header: () => null,
    cell: ({ row }) => {
      return (
        <Button
          variant="link"
          onClick={(e) => {
            e.stopPropagation();
            row.toggleExpanded();
          }}
          className="p-0 text-700"
          size="sm"
        >
          <FontAwesomeIcon
            icon={row.getIsExpanded() ? faChevronDown : faChevronRight}
            className="fs-9"
          />
        </Button>
      );
    },
    meta: {
      headerProps: { style: { width: "3%" } },
      cellProps: { className: "ps-4" },
    },
  },
  {
    accessorKey: "id",
    header: "ID",
    cell: ({ row: { original } }) => {
      return original.id;
    },
    meta: {
      headerProps: { style: { width: "5%" } },
      cellProps: { className: "fw-bold" },
    },
  },
  {
    accessorKey: "data",
    header: "Data",
    cell: ({ row: { original } }) => {
      return formatarDataVenda(original.data);
    },
    meta: {
      headerProps: { style: { width: "12%" } },
    },
  },
  {
    accessorKey: "empresaNomeFantasia",
    header: "Empresa",
    cell: ({ row: { original } }) => {
      return <Badge variant="tag">{original.empresaNomeFantasia}</Badge>;
    },
    meta: {
      headerProps: { style: { width: "10%" }, className: "text-center" },
      cellProps: { className: "text-center" },
    },
  },
  {
    accessorKey: "cliente",
    header: "Cliente",
    cell: ({ row: { original } }) => {
      return original.cliente;
    },
    meta: {
      headerProps: { style: { width: "15%" } },
    },
  },
  {
    accessorKey: "usuario",
    header: "Vendedor",
    cell: ({ row: { original } }) => {
      return original.usuario;
    },
    meta: {
      headerProps: { style: { width: "10%" } },
    },
  },
  {
    accessorKey: "valorTotal",
    header: "Valor Total",
    cell: ({ row: { original } }) => {
      return formatarValor(original.valorTotal);
    },
    meta: {
      headerProps: { style: { width: "10%" } },
      cellProps: { className: "fw-bold" },
    },
  },
  {
    accessorKey: "quantidadeItens",
    header: "Itens",
    cell: ({ row: { original } }) => {
      return original.quantidadeItens;
    },
    meta: {
      headerProps: { style: { width: "5%" }, className: "text-center" },
      cellProps: { className: "text-center" },
    },
  },
  {
    accessorKey: "numeroNFe",
    header: "Nota Fiscal",
    cell: ({ row: { original } }) => {
      return original.numeroNFe > 0 ? original.numeroNFe : "-";
    },
    meta: {
      headerProps: { style: { width: "10%" }, className: "text-center" },
      cellProps: { className: "text-center" },
    },
  },
  {
    accessorKey: "cancelado",
    header: "Status",
    cell: ({ row: { original } }) => {
      return (
        <Badge bg={getStatusVendaClass(original.cancelado)}>
          {formatarStatusVenda(original.cancelado)}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: "10%" }, className: "text-center" },
      cellProps: { className: "text-center" },
    },
  },
];

/**
 * Componente VendaTable - Tabela de vendas com expansão automática
 * 
 * Para expandir automaticamente uma venda específica, adicione o parâmetro 'vendaId' na URL:
 * Exemplo: /retaguarda/venda?vendaId=123
 * 
 * A tabela irá:
 * 1. Buscar pela venda específica usando o termo de busca
 * 2. Expandir automaticamente a linha correspondente
 * 3. Fazer scroll suave até a linha expandida
 */
const VendaTable = () => {
  const [searchParams] = useSearchParams();
  const table = useAdvanceTableContext<VendaType>();

  // ID da venda nos parâmetros da URL para expansão automática
  const vendaId = searchParams.get('vendaId');

  useEffect(() => {
    if (vendaId && table) {
      // Aguardar um pequeno delay para garantir que a tabela foi renderizada e os dados carregados
      const timer = setTimeout(() => {
        const rows = table.getRowModel().rows;

        // Verificar se há dados na tabela antes de tentar expandir
        if (rows.length > 0) {
          // Encontrar a linha correspondente ao ID da venda
          const targetRow = rows.find(row => row.original.id?.toString() === vendaId);

          if (targetRow && !targetRow.getIsExpanded()) {
            // Expandir a linha se ela não estiver expandida
            targetRow.toggleExpanded(true);

            // Opcional: scroll até a linha expandida após um breve delay
            setTimeout(() => {
              const rowElement = document.querySelector(`tr[data-index="${targetRow.index}"]`);
              if (rowElement) {
                rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
        }
      }, 300); // Aumentar o delay para garantir que os dados foram carregados

      return () => clearTimeout(timer);
    }
  }, [vendaId, table, table?.getRowModel().rows.length]);

  return (
    <div>
      <AdvanceTable
        tableProps={{ className: "phoenix-table fs-9" }}
        renderSubComponent={(row) => <VendaExpandedRow venda={row.original} />}
      />
      <AdvanceTableFooter pagination showViewAllBtn={false} />
    </div>
  );
};

export default VendaTable;
